import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PageWrapper from "../components/PageWrapper";
import "./ProjectsPage.css";
import { useSiteContext } from "../SiteContext";
import { Page } from "../interfaces/PagesInterface";
import config from "../configLoader";
import { Project } from "../interfaces/Project";
import ProjectsAPI from "../api/projects";
import {
    ProjectComplete,
    CompleteIntermediate,
    CompleteTask,
    ProjectPhaseAccountCode,
} from "../interfaces/ProjectCompleteInterface";
import { JobStatusEnum } from "../interfaces/V2Interface";
import { getJobStatus } from "../api/vistaBatchExport";
import ProjectMapPreview from "../components/ProjectMapPreview";
import ProjectGeolocationModal from "../components/ProjectGeolocationModal";
import { projectHasMapCoords } from "../utils/projectMapCoords";
import DollarUtil from "../util/DollarUtil";
import FilterBar, { FilterEmpty } from "../components/FilterBar";
import Highlight from "../components/Highlight";
import useDebouncedValue from "../hooks/useDebouncedValue";
import {
    allIntermediateIDs,
    countPhaseCodes,
    countTaskRows,
    filterCodesRegion,
    filterTasksRegion,
    normalizeTerm,
    FilteredNode,
    FilteredTask,
} from "../util/projectsFilter";
import {
    Building2,
    ChevronDown,
    MapPin,
    Layers,
    List,
    Clock,
    Database,
    Download,
    RefreshCw,
    Users,
    User,
    CheckCircle,
    AlertTriangle,
    DollarSign,
    ChevronRight,
    Info,
    Search,
    X,
    Check,
    TrendingUp,
    Inbox,
    Calendar,
    ArrowLeftRight,
} from "lucide-react";

// ─── Types ───────────────────────────────────────────────────────────────────

interface RecentProject {
    projectID: number;
    altProjectID: string;
    name: string;
    active: boolean;
}

interface ToastItem {
    id: number;
    kind: "ok" | "err";
    msg: string;
    out?: boolean;
}

interface SyncResult {
    added: number;
    updated: number;
    removed: number;
    status: string;
    scanned: number;
    elapsed: number;
    completedAt: string;
}

interface Rollup {
    total: number;
    completed: number;
    open: number;
    orig: number;
    rem: number;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const RECENTS_KEY = "prj_recent_projects";
const MAX_RECENTS = 6;
const DASH = "—";

// ─── Rollup math (mirrors rollTask / rollNode / rollProject from projects.js) ─

function rollTask(t: CompleteTask, acc: Rollup): void {
    acc.total++;
    if (t.remainingAmount === 0) acc.completed++;
    else acc.open++;
    acc.orig += t.originalAmount;
    acc.rem += t.remainingAmount;
}

function rollNode(node: CompleteIntermediate, acc: Rollup): void {
    (node.tasks || []).forEach((t) => rollTask(t, acc));
    (node.intermediates || []).forEach((c) => rollNode(c, acc));
}

function rollProject(c: ProjectComplete): Rollup {
    const acc: Rollup = { total: 0, completed: 0, open: 0, orig: 0, rem: 0 };
    (c.directTasks || []).forEach((t) => rollTask(t, acc));
    (c.intermediates || []).forEach((n) => rollNode(n, acc));
    return acc;
}

function rollNodeOnly(node: CompleteIntermediate): Rollup {
    const acc: Rollup = { total: 0, completed: 0, open: 0, orig: 0, rem: 0 };
    rollNode(node, acc);
    return acc;
}

// ─── Sort: numeric-first, non-numeric after, then lexical ────────────────────

function sortCodes(a: ProjectPhaseAccountCode, b: ProjectPhaseAccountCode): number {
    const sa = String(a.altAccountCodeID ?? "").trim();
    const sb = String(b.altAccountCodeID ?? "").trim();
    const na = parseFloat(sa);
    const nb = parseFloat(sb);
    const aNum = !isNaN(na) && /^\d/.test(sa);
    const bNum = !isNaN(nb) && /^\d/.test(sb);
    if (aNum && bNum) return na - nb;
    if (aNum) return -1;
    if (bNum) return 1;
    return sa.localeCompare(sb);
}

// ─── Utilities ───────────────────────────────────────────────────────────────

function fmtDateUTC(dateString: string): string {
    if (!dateString) return DASH;
    try {
        const d = new Date(dateString);
        if (isNaN(d.getTime())) return DASH;
        return `${d.getUTCMonth() + 1}/${d.getUTCDate()}/${d.getUTCFullYear()}`;
    } catch {
        return dateString;
    }
}

function fmtElapsed(secs: number): string {
    return `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, "0")}`;
}

function initials(first: string, last: string): string {
    return `${(first || " ")[0]}${(last || " ")[0]}`.toUpperCase();
}

function dashOrVal(v: string | number | null | undefined): string {
    if (v == null || String(v).trim() === "") return DASH;
    return String(v);
}

// ─── Recents localStorage helpers ────────────────────────────────────────────

function loadRecents(): RecentProject[] {
    try {
        const raw = localStorage.getItem(RECENTS_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed as RecentProject[];
    } catch {
        return [];
    }
}

function saveRecent(p: Project, current: RecentProject[]): RecentProject[] {
    const entry: RecentProject = {
        projectID: p.projectID,
        altProjectID: p.altProjectID,
        name: p.name,
        active: p.active,
    };
    const filtered = current.filter((r) => r.projectID !== p.projectID);
    const updated = [entry, ...filtered].slice(0, MAX_RECENTS);
    try {
        localStorage.setItem(RECENTS_KEY, JSON.stringify(updated));
    } catch {
        // ignore storage errors
    }
    return updated;
}

// ─── Piece sheet error message extraction (preserved from legacy) ─────────────

async function getPieceSheetErrorMessage(error: unknown): Promise<string> {
    const err = error as { response?: { data?: unknown } };
    const errorData = err?.response?.data;
    if (errorData instanceof Blob) {
        try {
            const text = await errorData.text();
            if (!text) return "Failed to download piece sheet.";
            const parsed = JSON.parse(text) as { message?: string };
            return parsed?.message || "Failed to download piece sheet.";
        } catch {
            return "Failed to download piece sheet.";
        }
    }
    const msg = (err?.response?.data as { message?: string })?.message;
    return msg || "Failed to download piece sheet.";
}

// ─── Component ───────────────────────────────────────────────────────────────

const ProjectsPage: React.FC = () => {
    const { setPage, createPopup, setLoading, userRole, projectIntent, setProjectIntent } = useSiteContext();

    // Permission flags (inline reads — viewProjectMap/editProjectMap not in AdminPermission union)
    const canViewProjectMap = userRole.viewProjectMap === true;
    const canEditProjectMap = userRole.editProjectMap === true;
    const canAccessMapUi = canViewProjectMap || canEditProjectMap;
    const canSyncPieces = config.enableSyncPieces && (userRole.admin === true);

    // ── Search / picker state ──────────────────────────────────────────────
    const [query, setQuery] = useState("");
    const [pickerOpen, setPickerOpen] = useState(false);
    const [searchResults, setSearchResults] = useState<Project[]>([]);
    const [searching, setSearching] = useState(false);
    /** Keyboard-highlighted result index; -1 = none (focus conceptually on the input). */
    const [highlightIdx, setHighlightIdx] = useState(-1);
    const pickerRef = useRef<HTMLDivElement>(null);
    const pickerInputWrapRef = useRef<HTMLDivElement>(null);
    /** Fixed-position coords for the floating results menu (escapes scroll containers). */
    const [menuPos, setMenuPos] = useState<{ left: number; top: number; width: number }>({ left: 0, top: 0, width: 0 });

    const placeMenu = useCallback(() => {
        const el = pickerInputWrapRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        setMenuPos({ left: r.left, top: r.bottom + 5, width: r.width });
    }, []);
    const debounceRef = useRef<number | null>(null);

    // ── Selection state ───────────────────────────────────────────────────
    const [view, setView] = useState<"welcome" | "selected">("welcome");
    const [selectedProject, setSelectedProject] = useState<Project | null>(null);
    const [projectData, setProjectData] = useState<ProjectComplete | null>(null);
    const [loadingProject, setLoadingProject] = useState(false);

    // ── Recents ──────────────────────────────────────────────────────────
    const [recents, setRecents] = useState<RecentProject[]>(() => loadRecents());

    // ── Breakdown expand state (collapsed by default) ─────────────────────
    const [expandedNodes, setExpandedNodes] = useState<Set<number>>(new Set());
    const [expandedTasks, setExpandedTasks] = useState<Set<number>>(new Set());
    const [expandedMiscGroups, setExpandedMiscGroups] = useState<Set<number>>(new Set());
    const [expandedPhases, setExpandedPhases] = useState<Set<number>>(new Set());

    // ── Per-card text filters (transient triage aids — no URL or storage state) ────
    const [tasksFilter, setTasksFilter] = useState("");
    const [codesFilter, setCodesFilter] = useState("");
    const tasksTerm = normalizeTerm(useDebouncedValue(tasksFilter));
    const codesTerm = normalizeTerm(useDebouncedValue(codesFilter));

    // ── Action loading state ──────────────────────────────────────────────
    const [syncingPieces, setSyncingPieces] = useState(false);
    const [downloadingPSheet, setDownloadingPSheet] = useState(false);
    const [downloadingMiscTasks, setDownloadingMiscTasks] = useState(false);

    // ── Sync JC state ─────────────────────────────────────────────────────
    const [syncRunning, setSyncRunning] = useState(false);
    const [syncStage, setSyncStage] = useState("Starting background job…");
    const [syncFill, setSyncFill] = useState(8);
    const [syncElapsed, setSyncElapsed] = useState(0);
    const [syncResultOpen, setSyncResultOpen] = useState(false);
    const [syncResult, setSyncResult] = useState<SyncResult | null>(null);
    const syncTimerRef = useRef<number | null>(null);
    const syncPollRef = useRef<number | null>(null);

    // ── Geo modal ──────────────────────────────────────────────────────────
    const [geoModalOpen, setGeoModalOpen] = useState(false);
    const [geoModalMode, setGeoModalMode] = useState<"view" | "edit">("view");

    // ── Toasts ────────────────────────────────────────────────────────────
    const [toasts, setToasts] = useState<ToastItem[]>([]);
    const toastIdRef = useRef(0);

    // ── Access gate ───────────────────────────────────────────────────────
    useEffect(() => {
        if (!config.showProjectsPage) {
            createPopup("Access Denied", "This feature is not available.");
            setPage(Page.Dashboard);
        }
    }, [setPage, createPopup]);

    // ── Click-outside closes picker ───────────────────────────────────────
    useEffect(() => {
        const handleDocClick = (e: MouseEvent) => {
            if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
                setPickerOpen(false);
            }
        };
        document.addEventListener("mousedown", handleDocClick);
        return () => document.removeEventListener("mousedown", handleDocClick);
    }, []);

    // ── Keep the fixed-position menu anchored while open ──────────────────
    useEffect(() => {
        if (!pickerOpen) return;
        placeMenu();
        window.addEventListener("resize", placeMenu);
        // capture-phase scroll catches the inner scroll containers (.v2-content / .page-wrapper)
        document.addEventListener("scroll", placeMenu, true);
        return () => {
            window.removeEventListener("resize", placeMenu);
            document.removeEventListener("scroll", placeMenu, true);
        };
    }, [pickerOpen, placeMenu]);

    // ── Cleanup sync timers on unmount ────────────────────────────────────
    useEffect(() => {
        return () => {
            if (syncTimerRef.current) clearInterval(syncTimerRef.current);
            if (syncPollRef.current) clearInterval(syncPollRef.current);
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, []);

    // ── Toast helper ──────────────────────────────────────────────────────
    const showToast = useCallback((kind: "ok" | "err", msg: string) => {
        const id = ++toastIdRef.current;
        setToasts((prev) => [...prev, { id, kind, msg }]);
        setTimeout(() => {
            setToasts((prev) => prev.map((t) => (t.id === id ? { ...t, out: true } : t)));
            setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 220);
        }, 3600);
    }, []);

    // ── Toggle helpers ────────────────────────────────────────────────────
    const toggleNode = useCallback((id: number) => {
        setExpandedNodes((s) => {
            const n = new Set(s);
            n.has(id) ? n.delete(id) : n.add(id);
            return n;
        });
    }, []);
    const toggleTask = useCallback((id: number) => {
        setExpandedTasks((s) => {
            const n = new Set(s);
            n.has(id) ? n.delete(id) : n.add(id);
            return n;
        });
    }, []);
    const toggleMiscGroup = useCallback((id: number) => {
        setExpandedMiscGroups((s) => {
            const n = new Set(s);
            n.has(id) ? n.delete(id) : n.add(id);
            return n;
        });
    }, []);
    const togglePhase = useCallback((id: number) => {
        setExpandedPhases((s) => {
            const n = new Set(s);
            n.has(id) ? n.delete(id) : n.add(id);
            return n;
        });
    }, []);

    // ── Filtered views of the two breakdown cards ─────────────────────────
    const phasesWithCodes = useMemo(
        () => (projectData?.projectPhasesAccountCodes ?? []).filter((ph) => (ph.accountCodes?.length ?? 0) > 0),
        [projectData]
    );
    const tasksView = useMemo(
        () => (projectData ? filterTasksRegion(projectData, tasksTerm) : null),
        [projectData, tasksTerm]
    );
    const codesView = useMemo(
        () => filterCodesRegion(phasesWithCodes, codesTerm),
        [phasesWithCodes, codesTerm]
    );
    const totalTaskRows = useMemo(() => (projectData ? countTaskRows(projectData) : 0), [projectData]);
    const totalPhaseCodes = useMemo(() => countPhaseCodes(phasesWithCodes), [phasesWithCodes]);

    /*
     * Filtering overrides collapse state for the rows it needs to reveal, and that becomes
     * the user's new baseline — clearing the term does not collapse them again. So the
     * reveals merge straight into the same sets a click writes to.
     */
    useEffect(() => {
        if (!tasksView || !tasksTerm) return;
        const { revealNodes, revealTasks, revealMisc } = tasksView;
        if (revealNodes.length) setExpandedNodes((s) => new Set([...s, ...revealNodes]));
        if (revealTasks.length) setExpandedTasks((s) => new Set([...s, ...revealTasks]));
        if (revealMisc.length) setExpandedMiscGroups((s) => new Set([...s, ...revealMisc]));
    }, [tasksView, tasksTerm]);

    useEffect(() => {
        if (!codesTerm || codesView.revealPhases.length === 0) return;
        setExpandedPhases((s) => new Set([...s, ...codesView.revealPhases]));
    }, [codesView, codesTerm]);

    // Expand all opens the containers — intermediates, misc groups, phases — and leaves
    // task rows alone so a 100-task project stays scannable.
    const expandAllTasks = useCallback((open: boolean) => {
        if (!projectData) return;
        if (!open) {
            setExpandedNodes(new Set());
            setExpandedMiscGroups(new Set());
            return;
        }
        setExpandedNodes(new Set(allIntermediateIDs(projectData.intermediates ?? [])));
        setExpandedMiscGroups(new Set((projectData.miscEmployeeTasks ?? []).map((g) => g.accountCode.accountCodeID)));
    }, [projectData]);

    const expandAllPhases = useCallback((open: boolean) => {
        setExpandedPhases(open ? new Set(phasesWithCodes.map((ph) => ph.projectPhaseID)) : new Set());
    }, [phasesWithCodes]);

    // ── Search ────────────────────────────────────────────────────────────
    const doSearch = useCallback(async (q: string) => {
        if (q.trim().length < 2) {
            setSearchResults([]);
            setPickerOpen(false);
            return;
        }
        setSearching(true);
        setPickerOpen(true);
        try {
            const results = await ProjectsAPI.typeahead(q);
            setSearchResults(results);
        } catch {
            setSearchResults([]);
        } finally {
            setSearching(false);
            setHighlightIdx(-1);
        }
    }, []);

    const handleQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const v = e.target.value;
        setQuery(v);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = window.setTimeout(() => doSearch(v), 300);
    };

    const handlePickerKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "ArrowDown") {
            e.preventDefault();
            if (!pickerOpen && searchResults.length > 0) {
                setPickerOpen(true);
                setHighlightIdx(0);
                return;
            }
            setHighlightIdx((i) => Math.min(i + 1, searchResults.length - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlightIdx((i) => Math.max(i - 1, -1));
        } else if (e.key === "Enter") {
            e.preventDefault();
            if (debounceRef.current) clearTimeout(debounceRef.current);
            if (searchResults.length > 0) {
                // select the keyboard-highlighted row; default to the first result
                handleSelectProject(searchResults[highlightIdx >= 0 ? highlightIdx : 0]);
            } else {
                doSearch(query);
            }
        } else if (e.key === "Escape") {
            setPickerOpen(false);
            setHighlightIdx(-1);
        }
    };

    // Keep the keyboard-highlighted option scrolled into view.
    useEffect(() => {
        if (highlightIdx < 0) return;
        pickerRef.current
            ?.querySelector(".prj-picker-opt.hi")
            ?.scrollIntoView({ block: "nearest" });
    }, [highlightIdx]);

    // ── Project selection ─────────────────────────────────────────────────
    const handleSelectProject = useCallback(
        async (project: Project) => {
            setPickerOpen(false);
            setQuery("");
            setSearchResults([]);
            setSelectedProject(project);
            setProjectData(null);
            setView("selected");
            setGeoModalOpen(false);
            setExpandedNodes(new Set());
            setExpandedTasks(new Set());
            setExpandedMiscGroups(new Set());
            setExpandedPhases(new Set());
            setTasksFilter("");
            setCodesFilter("");
            setRecents((prev) => saveRecent(project, prev));

            setLoadingProject(true);
            try {
                const resp = await ProjectsAPI.getCompleteProject(project.projectID);
                setProjectData(resp.data);
            } catch {
                showToast("err", "Failed to load project data.");
            } finally {
                setLoadingProject(false);
            }
        },
        [showToast]
    );

    // ── Project Map "Open in Projects" intent ─────────────────────────────
    // The map popup sets projectIntent then navigates here; look the project up
    // (same typeahead path as recent chips) and open it. Cleared immediately so
    // it never re-fires on remount.
    useEffect(() => {
        if (!projectIntent) return;
        const intent = projectIntent;
        setProjectIntent(null);
        (async () => {
            setLoadingProject(true);
            try {
                const results = await ProjectsAPI.typeahead(intent.altProjectID);
                const found = results.find((r) => r.projectID === intent.projectID);
                if (found) {
                    await handleSelectProject(found);
                } else {
                    showToast("err", `Project <b>${intent.altProjectID}</b> not found.`);
                }
            } catch {
                showToast("err", "Failed to open project from the map.");
            } finally {
                setLoadingProject(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectIntent]);

    // ── Recent chip click ─────────────────────────────────────────────────
    const handleRecentClick = useCallback(
        async (recent: RecentProject) => {
            setLoadingProject(true);
            try {
                const results = await ProjectsAPI.typeahead(recent.altProjectID);
                const found = results.find((r) => r.projectID === recent.projectID);
                if (found) {
                    await handleSelectProject(found);
                    return;
                }
                // Project not found — remove from recents and show error
                setRecents((prev) => {
                    const updated = prev.filter((r) => r.projectID !== recent.projectID);
                    try { localStorage.setItem(RECENTS_KEY, JSON.stringify(updated)); } catch {}
                    return updated;
                });
                showToast("err", `Project <b>${recent.altProjectID}</b> not found — it may have been removed.`);
            } catch {
                showToast("err", "Failed to look up project.");
            } finally {
                setLoadingProject(false);
            }
        },
        [handleSelectProject, showToast]
    );

    // ── Change project ────────────────────────────────────────────────────
    const handleChangeProject = () => {
        setView("welcome");
        setSelectedProject(null);
        setProjectData(null);
        setQuery("");
        setSearchResults([]);
        setPickerOpen(false);
        setGeoModalOpen(false);
    };

    // ── Download PSheet ───────────────────────────────────────────────────
    const handleDownloadPSheet = async () => {
        if (!selectedProject || downloadingPSheet) return;
        setDownloadingPSheet(true);
        try {
            const { blob } = await ProjectsAPI.downloadPieceSheet(selectedProject.projectID);
            const today = new Date();
            const fileName = `PSheet-${selectedProject.altProjectID}-${today.getMonth() + 1}-${today.getDate()}-${today.getFullYear()}.xlsx`;
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            showToast("ok", `PSheet downloaded — <b>${fileName}</b>`);
        } catch (error) {
            const msg = await getPieceSheetErrorMessage(error);
            showToast("err", msg);
        } finally {
            setDownloadingPSheet(false);
        }
    };

    // ── Download Misc Tasks ───────────────────────────────────────────────
    const handleDownloadMiscTasks = async () => {
        if (!selectedProject || downloadingMiscTasks) return;
        setDownloadingMiscTasks(true);
        try {
            const { blob } = await ProjectsAPI.downloadMiscTasks(selectedProject.projectID);
            const today = new Date();
            const fileName = `MiscTasks-${selectedProject.altProjectID}-${today.getMonth() + 1}-${today.getDate()}-${today.getFullYear()}.xlsx`;
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            showToast("ok", `Misc tasks downloaded — <b>${fileName}</b>`);
        } catch {
            showToast("err", "Failed to download the misc tasks export.");
        } finally {
            setDownloadingMiscTasks(false);
        }
    };

    // ── Sync Pieces ───────────────────────────────────────────────────────
    const handleSyncPieces = async () => {
        if (!selectedProject?.altProjectID2 || syncingPieces) return;
        setSyncingPieces(true);
        try {
            const response = await ProjectsAPI.projectAddUpdatePieces(
                selectedProject.altProjectID,
                Number(selectedProject.altProjectID2)
            );
            const data = response.data;
            if (data.success) {
                const u = data.updates;
                const summary = u
                    ? `<b>${u.piecesAdded} added</b>, ${u.piecesUpdated} updated, ${u.piecesRemoved} removed. Breakdown refreshed.`
                    : "Pieces synced. Breakdown refreshed.";
                showToast("ok", `Pieces synced — ${summary}`);
                const refreshResp = await ProjectsAPI.getCompleteProject(selectedProject.projectID);
                setProjectData(refreshResp.data);
            } else {
                showToast("err", data.message || "Sync failed.");
            }
        } catch {
            showToast("err", "Failed to sync pieces. Please try again.");
        } finally {
            setSyncingPieces(false);
        }
    };

    // ── Sync JC Pieceworks Cache ──────────────────────────────────────────
    const handleSyncJC = async () => {
        if (syncRunning) return;
        setSyncRunning(true);
        setSyncStage("Starting background job…");
        setSyncFill(8);
        setSyncElapsed(0);
        const startTime = Date.now();

        syncTimerRef.current = window.setInterval(() => {
            setSyncElapsed(Math.floor((Date.now() - startTime) / 1000));
        }, 500);

        let jobId: number;
        try {
            const { jobID } = await ProjectsAPI.refreshPieceCache();
            jobId = jobID;
            setSyncStage("Job queued · polling every 5s");
            setSyncFill(12);
        } catch {
            clearInterval(syncTimerRef.current!);
            setSyncRunning(false);
            showToast("err", "Failed to start sync job.");
            return;
        }

        const stageFromProgress = (p: number): string => {
            if (p < 25) return "Reading job cost records…";
            if (p < 55) return "Reconciling piece definitions…";
            return "Writing cache · finalizing…";
        };

        syncPollRef.current = window.setInterval(async () => {
            try {
                const status = await getJobStatus(jobId);
                const pct = Math.min(90, status.progress || 12);
                setSyncFill(pct);
                setSyncStage(status.currentStep || stageFromProgress(pct));

                if (status.status === JobStatusEnum.COMPLETED) {
                    clearInterval(syncTimerRef.current!);
                    clearInterval(syncPollRef.current!);
                    const elapsed = Math.floor((Date.now() - startTime) / 1000);
                    const rd = (status.resultData ?? {}) as Record<string, unknown>;
                    setSyncResult({
                        added:       Number(rd.piecesAdded   ?? rd.added   ?? 0),
                        updated:     Number(rd.piecesUpdated ?? rd.updated ?? 0),
                        removed:     Number(rd.piecesRemoved ?? rd.removed ?? 0),
                        status:      "COMPLETED",
                        scanned:     Number(rd.recordsScanned ?? rd.totalRecords ?? 0),
                        elapsed,
                        completedAt: status.completedDateTime ?? "",
                    });
                    setSyncFill(100);
                    setSyncRunning(false);
                    setSyncResultOpen(true);
                    showToast("ok", "JC Pieceworks cache sync <b>completed</b>.");
                } else if (status.status === JobStatusEnum.FAILED) {
                    clearInterval(syncTimerRef.current!);
                    clearInterval(syncPollRef.current!);
                    setSyncRunning(false);
                    showToast("err", "JC Pieceworks cache sync <b>failed</b>.");
                }
            } catch {
                // continue polling on transient error
            }
        }, 5000);
    };

    // ─────────────────────────────────────────────────────────────────────────
    // RENDER HELPERS
    // ─────────────────────────────────────────────────────────────────────────

    const renderPicker = (pickerId: string) => (
        <div className="prj-picker" id={pickerId} ref={pickerRef}>
            <span className="prj-pick-label">Find a project</span>
            <div className="prj-picker-input" ref={pickerInputWrapRef} onClick={() => {
                const input = document.getElementById(`${pickerId}-input`) as HTMLInputElement;
                if (input) input.focus();
            }}>
                <Search size={16} />
                <input
                    id={`${pickerId}-input`}
                    type="text"
                    placeholder="Search by project ID or name…"
                    value={query}
                    onChange={handleQueryChange}
                    onKeyDown={handlePickerKeyDown}
                    onFocus={() => { if (searchResults.length > 0) setPickerOpen(true); }}
                    autoComplete="off"
                />
            </div>
            <div className="prj-picker-hint">
                <Info size={14} />
                Includes inactive projects. Type to filter, Enter or click to open.
            </div>
            {pickerOpen && (
                <div
                    className="prj-picker-menu"
                    style={{ left: menuPos.left, top: menuPos.top, width: menuPos.width }}
                >
                    {searching ? (
                        <div className="prj-picker-msg">
                            <span className="prj-spin" />
                            Searching…
                        </div>
                    ) : searchResults.length === 0 ? (
                        <div className="prj-picker-msg">No matching projects found.</div>
                    ) : (
                        searchResults.map((r, idx) => (
                            <div
                                key={r.projectID}
                                className={`prj-picker-opt${idx === highlightIdx ? " hi" : ""}`}
                                onMouseDown={(e) => { e.preventDefault(); handleSelectProject(r); }}
                                onMouseEnter={() => setHighlightIdx(idx)}
                            >
                                <div className="prj-grow">
                                    <span className="prj-po-code">{r.altProjectID}</span>
                                    {" · "}
                                    <span className="prj-po-name">{r.name}</span>
                                </div>
                                <span className={`prj-pill ${r.active ? "active" : "inactive"}`}>
                                    <span className="dot" />
                                    {r.active ? "Active" : "Inactive"}
                                </span>
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );

    // ── Welcome view ──────────────────────────────────────────────────────
    const renderWelcome = () => (
        <div className="prj-welcome-outer">
            <div className="prj-welcome-card prj-card">
                <div className="prj-welcome">
                    <div className="prj-w-ic">
                        <Building2 />
                    </div>
                    <h2>Select a project to begin</h2>
                    <p>
                        Look up any project to see its supervisors, location and geofence,
                        and the full piece-work &amp; hourly cost-code breakdown.
                    </p>
                    <div className="prj-pick-wrap">
                        {renderPicker("welcomePicker")}
                    </div>
                    {recents.length > 0 && (
                        <div className="prj-recent-line">
                            <div className="prj-recent-h">Recent</div>
                            <div className="prj-recent-chips">
                                {recents.map((r) => (
                                    <button
                                        key={r.projectID}
                                        className={`prj-recent-chip ${r.active ? "" : "inactive"}`}
                                        onClick={() => handleRecentClick(r)}
                                    >
                                        <span className="prj-rc-code">{r.altProjectID}</span>
                                        {r.name}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    // ── Task row ──────────────────────────────────────────────────────────
    const renderTaskRow = (ft: FilteredTask) => {
        const { task } = ft;
        const term = tasksTerm;
        const done = task.remainingAmount === 0;
        const claimsCount = (task.employeesClaimed || []).length;
        const open = expandedTasks.has(task.taskID);
        const sortedClaims = [...(task.employeesClaimed || [])].sort(
            (a, b) => new Date(a.entryDate).getTime() - new Date(b.entryDate).getTime()
        );
        return (
            <div key={task.taskID} className={`prj-task-row ${done ? "done" : ""} ${open ? "open" : ""}`}>
                <div className="prj-task-line" onClick={() => toggleTask(task.taskID)}>
                    <ChevronRight className="caret" size={15} />
                    <div className="prj-t-check">
                        {done && <Check size={12} />}
                    </div>
                    <span className="prj-t-acct">
                        <Highlight text={task.account.altAccountCodeID} term={term} />
                    </span>
                    <span className="prj-t-name">
                        <Highlight text={task.taskInfo.name} term={term} />
                        {task.taskInfo.description && (
                            <div className="prj-t-desc">
                                <Highlight text={task.taskInfo.description} term={term} />
                            </div>
                        )}
                    </span>
                    {claimsCount > 0 && (
                        <span className="prj-t-claims">
                            <User size={12} />
                            {claimsCount} claimed
                        </span>
                    )}
                    <span className="prj-t-mid">
                        <span className="prj-t-amt orig">{DollarUtil.formatDollar(task.originalAmount)}</span>
                        <span className="prj-t-amt rem">{DollarUtil.formatDollar(task.remainingAmount)}</span>
                    </span>
                </div>
                <div className="prj-claims">
                    <div className="prj-cl-h">Employees claimed · oldest first</div>
                    {sortedClaims.length === 0 ? (
                        <div className="prj-claims-empty">No employee claims yet.</div>
                    ) : (
                        sortedClaims.map((cl, i) => (
                            <div key={i} className="prj-claim">
                                <span className="prj-cl-who">
                                    <Highlight text={`${cl.name.first} ${cl.name.last}`} term={term} />
                                    <span className="code">
                                        <Highlight text={cl.altEmployeeID} term={term} />
                                    </span>
                                </span>
                                <span className="prj-cl-amt">{DollarUtil.formatDollar(cl.amountClaimed)}</span>
                                <span className="prj-cl-date">{fmtDateUTC(cl.entryDate)}</span>
                                <span className="prj-cl-week">
                                    {cl.weekNumber != null && (
                                        <span className="prj-week-pill">W{cl.weekNumber}</span>
                                    )}
                                </span>
                            </div>
                        ))
                    )}
                </div>
            </div>
        );
    };

    // ── Recursive intermediate node ────────────────────────────────────────
    const renderNode = (fn: FilteredNode, depth: number): React.ReactNode => {
        const { node, mark } = fn;
        const term = tasksTerm;
        const roll = rollNodeOnly(node);
        const collapsed = !expandedNodes.has(node.intermediateID); // default collapsed = not in expanded set
        const markCls = mark === "match" ? " fmatch" : mark === "context" ? " fctx" : "";
        return (
            <div
                key={node.intermediateID}
                className={`prj-node ${collapsed ? "collapsed" : ""}${markCls}`}
                data-depth={depth}
            >
                <div className="prj-node-head" onClick={() => toggleNode(node.intermediateID)}>
                    <ChevronRight className="caret" size={16} />
                    <span className="prj-n-ic">
                        <Layers size={15} />
                    </span>
                    <span className="prj-n-name">
                        <Highlight text={node.name} term={term} />
                        <span className="prj-n-code">
                            <Highlight text={node.altIntermediateID} term={term} />
                        </span>
                    </span>
                    <span className="prj-n-rollup">
                        <span className="prj-n-tasks">
                            <b>{roll.completed}</b> done · {roll.open} open
                        </span>
                        <span className="prj-n-amt orig">{DollarUtil.formatDollar(roll.orig)}</span>
                        <span className="prj-n-amt rem">{DollarUtil.formatDollar(roll.rem)}</span>
                    </span>
                </div>
                <div className="prj-node-kids">
                    {fn.tasks.map((t) => renderTaskRow(t))}
                    {fn.children.map((c) => renderNode(c, depth + 1))}
                </div>
            </div>
        );
    };

    // ── Breakdown card 1: Tasks & Piece Work ──────────────────────────────
    const renderBreakdownCard1 = (c: ProjectComplete, roll: Rollup) => {
        // Header counts and totals describe the whole project — the filter never touches them.
        const directN = (c.directTasks || []).length;
        const interN = (c.intermediates || []).length;
        const hasTasks = directN > 0 || interN > 0;
        const miscGroups = c.miscEmployeeTasks || [];
        const miscItemCount = miscGroups.reduce((s, g) => s + g.tasks.length, 0);
        const miscTotal = miscGroups.reduce((s, g) => s + g.tasks.reduce((a, t) => a + t.amountClaimed, 0), 0);
        const hasContent = hasTasks || miscGroups.length > 0;

        // Filtered view: sections drop out when the term leaves them with no rows.
        const view = tasksView;
        const shownDirect = view?.directTasks ?? [];
        const shownNodes = view?.nodes ?? [];
        const shownMisc = view?.miscGroups ?? [];
        const filtering = tasksTerm !== "";
        const showTasksSection = hasTasks && (shownDirect.length > 0 || shownNodes.length > 0);
        const showMiscSection = shownMisc.length > 0;
        const noMatches = filtering && hasContent && !showTasksSection && !showMiscSection;

        return (
            <div className="prj-bd-card">
                <div className="prj-bd-head">
                    <span className="prj-bh-ic"><List size={18} /></span>
                    <div>
                        <h3>Tasks &amp; Piece Work</h3>
                        <div className="prj-bd-sub">Direct tasks, intermediate tree, and miscellaneous employee claims</div>
                    </div>
                    <div className="prj-bh-right">
                        <div className="prj-bh-metric">
                            <div className="m-val" style={{ color: "var(--text-3)" }}>{DollarUtil.formatDollar(roll.orig)}</div>
                            <div className="m-lab">Original</div>
                        </div>
                        <div className="prj-bh-metric">
                            <div className="m-val">{DollarUtil.formatDollar(roll.rem)}</div>
                            <div className="m-lab">Remaining</div>
                        </div>
                    </div>
                </div>
                {hasContent && (
                    <FilterBar
                        value={tasksFilter}
                        onChange={setTasksFilter}
                        placeholder="Filter tasks — name, description, code, employee…"
                        label="Filter tasks"
                        matches={view?.matches ?? 0}
                        total={totalTaskRows}
                        active={filtering}
                    >
                        <span className="fb-acts">
                            <button type="button" className="fb-btn" onClick={() => expandAllTasks(true)}>
                                <ChevronDown size={14} /> Expand all
                            </button>
                            <button type="button" className="fb-btn" onClick={() => expandAllTasks(false)}>
                                <ChevronRight size={14} /> Collapse all
                            </button>
                        </span>
                    </FilterBar>
                )}
                <div className="prj-bd-body">
                    {!hasContent ? (
                        <div className="prj-bd-empty">
                            <Inbox size={30} />
                            <div className="prj-be-t">No tasks found for this project.</div>
                        </div>
                    ) : noMatches ? (
                        <FilterEmpty noun="tasks" term={tasksTerm} onClear={() => setTasksFilter("")} />
                    ) : (
                        <>
                            {showTasksSection && (
                                <>
                                    <div className="prj-sec-head">
                                        <span className="sh-t">Tasks</span>
                                        <span className="sh-count">{directN + interN}</span>
                                        <span className="sh-cols">
                                            <span className="c">Original</span>
                                            <span className="c">Remaining</span>
                                        </span>
                                    </div>
                                    {shownDirect.map((t) => renderTaskRow(t))}
                                    {shownNodes.length > 0 && (
                                        <div className="prj-tree">
                                            {shownNodes.map((n) => renderNode(n, 1))}
                                        </div>
                                    )}
                                </>
                            )}
                            {showMiscSection && (
                                <>
                                    <div className="prj-sec-head attn">
                                        <AlertTriangle size={15} />
                                        <span className="sh-t">Miscellaneous Tasks</span>
                                        <span className="sh-count">{miscItemCount}</span>
                                        <span className="sh-total">{DollarUtil.formatDollar(miscTotal)}</span>
                                    </div>
                                    {shownMisc.map((fg) => {
                                        const g = fg.group;
                                        // Group total is the group's real total, not the filtered subset's.
                                        const groupTotal = g.tasks.reduce((s, t) => s + t.amountClaimed, 0);
                                        const grpOpen = expandedMiscGroups.has(g.accountCode.accountCodeID);
                                        const markCls = fg.mark === "match" ? " fmatch" : fg.mark === "context" ? " fctx" : "";
                                        return (
                                            <div
                                                key={g.accountCode.accountCodeID}
                                                className={`prj-misc-group attn ${grpOpen ? "open" : ""}${markCls}`}
                                            >
                                                <div
                                                    className="prj-misc-head"
                                                    onClick={() => toggleMiscGroup(g.accountCode.accountCodeID)}
                                                >
                                                    <ChevronRight className="caret" size={16} />
                                                    <span className="prj-m-code">
                                                        <Highlight text={g.accountCode.altAccountCodeID} term={tasksTerm} />
                                                    </span>
                                                    <span className="prj-m-name">
                                                        <Highlight text={g.accountCode.name} term={tasksTerm} />
                                                    </span>
                                                    <span className="prj-m-total">{DollarUtil.formatDollar(groupTotal)}</span>
                                                </div>
                                                <div className="prj-misc-body">
                                                    {fg.items.map((t) => (
                                                        <div key={t.miscEmployeeTaskID} className="prj-misc-item">
                                                            <div className="prj-mi-top">
                                                                <span className="prj-mi-who">
                                                                    <Highlight
                                                                        text={`${t.employeeName.first} ${t.employeeName.last}`}
                                                                        term={tasksTerm}
                                                                    />
                                                                    <span className="code">
                                                                        <Highlight text={t.altEmployeeID} term={tasksTerm} />
                                                                    </span>
                                                                </span>
                                                                <span className="prj-mi-amt">{DollarUtil.formatDollar(t.amountClaimed)}</span>
                                                            </div>
                                                            <div className="prj-mi-meta">
                                                                <span className="prj-mono">{fmtDateUTC(t.entryDate)}</span>
                                                                {t.weekNumber != null && (
                                                                    <span className="prj-week-pill">W{t.weekNumber}</span>
                                                                )}
                                                                {t.intermediateName && (
                                                                    <span>
                                                                        <Highlight text={t.intermediateName} term={tasksTerm} />
                                                                    </span>
                                                                )}
                                                            </div>
                                                            {t.miscDescription && (
                                                                <div className="prj-mi-desc">
                                                                    <Highlight text={t.miscDescription} term={tasksTerm} />
                                                                    {t.superDescription && (
                                                                        <span className="prj-mi-sup-tag">
                                                                            {" — "}
                                                                            <Highlight text={t.superDescription} term={tasksTerm} />
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </>
                            )}
                        </>
                    )}
                </div>
            </div>
        );
    };

    // ── Breakdown card 2: Hourly Cost Codes ───────────────────────────────
    const renderBreakdownCard2 = () => {
        if (phasesWithCodes.length === 0) return null;
        // Phase and code counts in the header are the project's, not the filter's.
        const totalCodes = totalPhaseCodes;
        const filtering = codesTerm !== "";
        const noMatches = filtering && codesView.phases.length === 0;

        return (
            <div className="prj-bd-card">
                <div className="prj-bd-head">
                    <span className="prj-bh-ic warn"><Clock size={18} /></span>
                    <div>
                        <h3>Hourly Cost Codes</h3>
                        <div className="prj-bd-sub">Account codes available for hourly time, by project phase</div>
                    </div>
                    <div className="prj-bh-right">
                        <div className="prj-bh-metric">
                            <div className="m-val">{phasesWithCodes.length}</div>
                            <div className="m-lab">Phases · {totalCodes} codes</div>
                        </div>
                    </div>
                </div>
                <FilterBar
                    value={codesFilter}
                    onChange={setCodesFilter}
                    placeholder="Filter cost codes — phase, code, description…"
                    label="Filter cost codes"
                    matches={codesView.matches}
                    total={totalCodes}
                    active={filtering}
                >
                    <span className="fb-acts">
                        <button type="button" className="fb-btn" onClick={() => expandAllPhases(true)}>
                            <ChevronDown size={14} /> Expand all
                        </button>
                        <button type="button" className="fb-btn" onClick={() => expandAllPhases(false)}>
                            <ChevronRight size={14} /> Collapse all
                        </button>
                    </span>
                </FilterBar>
                <div className="prj-bd-body">
                    {noMatches ? (
                        <FilterEmpty noun="cost codes" term={codesTerm} onClear={() => setCodesFilter("")} />
                    ) : (
                        codesView.phases.map((fp) => {
                            const ph = fp.phase;
                            const codes = [...fp.codes].sort(sortCodes);
                            const phOpen = expandedPhases.has(ph.projectPhaseID);
                            const markCls = fp.mark === "match" ? " fmatch" : fp.mark === "context" ? " fctx" : "";
                            return (
                                <div
                                    key={ph.projectPhaseID}
                                    className={`prj-phase ${phOpen ? "open" : ""}${markCls}`}
                                >
                                    <div
                                        className="prj-phase-head"
                                        onClick={() => togglePhase(ph.projectPhaseID)}
                                    >
                                        <ChevronRight className="caret" size={16} />
                                        <span className="prj-ph-ic"><Clock size={16} /></span>
                                        <span className="prj-p-name">
                                            <Highlight text={ph.name} term={codesTerm} />
                                            <span className="prj-p-id">
                                                <Highlight text={ph.altProjectPhaseID} term={codesTerm} />
                                            </span>
                                        </span>
                                        <span className="prj-p-count">
                                            {/* the phase's own code count, not the filtered subset's */}
                                            {(ph.accountCodes?.length ?? 0)} codes
                                        </span>
                                    </div>
                                    <div className="prj-phase-body">
                                        {codes.map((a) => (
                                            <div key={a.accountCodeID} className="prj-code-row">
                                                <span className="prj-cr-id">
                                                    <Highlight text={a.altAccountCodeID} term={codesTerm} />
                                                </span>
                                                <span>
                                                    <span className="prj-cr-name">
                                                        <Highlight text={a.name} term={codesTerm} />
                                                    </span>
                                                    {a.description && (
                                                        <div className="prj-cr-desc">
                                                            <Highlight text={a.description} term={codesTerm} />
                                                        </div>
                                                    )}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
        );
    };

    // ── Selected project view ─────────────────────────────────────────────
    const renderSelected = () => {
        if (!selectedProject) return null;
        const p = selectedProject;
        const hasCoords = projectHasMapCoords(p);
        const roll = projectData ? rollProject(projectData) : null;
        const pct = roll && roll.orig > 0 ? Math.round(((roll.orig - roll.rem) / roll.orig) * 100) : 0;
        const claimed = roll ? roll.orig - roll.rem : 0;
        const supervisors = projectData?.supervisors ?? [];

        // Pay-group attachment is independent of the PW flag since TAS-150 (PW now comes from
        // Sage actrec.crtfid). Only worth showing when one of the two is actually true —
        // otherwise every ordinary job would carry a meaningless "(no pay groups)".
        const payGroupCount = projectData?.payGroupCount ?? 0;
        const showPayGroups = p.isPrevailingWage || payGroupCount > 0;
        const payGroupLabel = payGroupCount === 0
            ? "no pay groups"
            : `${payGroupCount} pay group${payGroupCount === 1 ? "" : "s"}`;

        // classif can be number or string — display as-is
        const classifDisplay = p.projectClassification != null ? String(p.projectClassification) : null;

        return (
            <>
                {/* Breadcrumb */}
                <div className="prj-crumbs">
                    <span>Setup</span>
                    <ChevronRight size={14} />
                    <span className="here">Projects</span>
                </div>

                {/* Identity header */}
                <div className="prj-head">
                    <div className="prj-head-logo">
                        <Building2 size={26} />
                    </div>
                    <div className="prj-head-main">
                        <div className="prj-head-id">
                            <span className="prj-mono">{p.altProjectID}</span>
                            <span className={`prj-pill ${p.active ? "active" : "inactive"}`}>
                                <span className="dot" />
                                {p.active ? "Active" : "Inactive"}
                            </span>
                        </div>
                        <h1>{p.name}</h1>
                        <div className="prj-head-sub">
                            {classifDisplay && <span>{classifDisplay}</span>}
                            {classifDisplay && p.department && <span className="sep" />}
                            {p.department && <span>{p.department}</span>}
                            {(classifDisplay || p.department) && p.startDate && <span className="sep" />}
                            {p.startDate && <span>Started {fmtDateUTC(p.startDate)}</span>}
                            {p.altProjectID2 && (
                                <>
                                    <span className="sep" />
                                    <span>JC <span className="prj-mono">{p.altProjectID2}</span></span>
                                </>
                            )}
                        </div>
                    </div>
                    <div className="prj-head-right">
                        <button className="prj-btn" onClick={handleChangeProject}>
                            <ArrowLeftRight size={16} />
                            Change project
                        </button>
                    </div>
                </div>

                {/* Action bar */}
                {(config.downloadPSheet || config.showMiscTaskExport || canSyncPieces) && (
                    <div className="prj-action-bar">
                        {config.downloadPSheet && (
                            <button
                                className="prj-btn"
                                onClick={handleDownloadPSheet}
                                disabled={downloadingPSheet}
                            >
                                {downloadingPSheet ? <span className="prj-spin" /> : <Download size={16} />}
                                Download PSheet
                            </button>
                        )}
                        {config.showMiscTaskExport && (
                            <button
                                className="prj-btn"
                                onClick={handleDownloadMiscTasks}
                                disabled={downloadingMiscTasks}
                            >
                                {downloadingMiscTasks ? <span className="prj-spin" /> : <Download size={16} />}
                                Download Misc Tasks
                            </button>
                        )}
                        {canSyncPieces && (
                            <>
                                <span
                                    className="prj-tipwrap"
                                    data-tip={
                                        !p.altProjectID2
                                            ? "This project has no secondary JC ID (altProjectID2), so pieces cannot be synced."
                                            : undefined
                                    }
                                >
                                    <button
                                        className="prj-btn"
                                        onClick={handleSyncPieces}
                                        disabled={!p.altProjectID2 || syncingPieces}
                                    >
                                        {syncingPieces ? <span className="prj-spin" /> : <RefreshCw size={16} />}
                                        Sync Pieces
                                    </button>
                                </span>
                                <button
                                    className="prj-btn"
                                    onClick={handleSyncJC}
                                    disabled={syncRunning}
                                >
                                    <Database size={16} />
                                    Sync JC Pieceworks Cache
                                </button>
                            </>
                        )}
                    </div>
                )}

                {/* Sync progress indicator */}
                {syncRunning && (
                    <div className="prj-sync-progress">
                        <span className="prj-spin" />
                        <div className="prj-sp-main">
                            <div className="prj-sp-t">Syncing JC Pieceworks cache…</div>
                            <div className="prj-sp-s">{syncStage}</div>
                            <div className="prj-sp-bar">
                                <span className="prj-sp-fill" style={{ width: `${syncFill}%` }} />
                            </div>
                        </div>
                        <span className="prj-sp-elapsed">{fmtElapsed(syncElapsed)}</span>
                    </div>
                )}

                {/* Detail grid */}
                <div className={`prj-detail-grid ${!canAccessMapUi ? "no-map" : ""}`}
                     style={!canAccessMapUi ? { gridTemplateColumns: "1fr 1fr" } : undefined}>
                    {/* Overview + Supervisors panel */}
                    <div className="prj-panel">
                        <div className="prj-panel-h">
                            <Info size={15} />
                            <span className="prj-pt">Overview</span>
                        </div>
                        <div className="prj-panel-b">
                            <div className="prj-info-row">
                                <span className="il">Project ID</span>
                                <span className="iv prj-mono">{p.altProjectID}</span>
                            </div>
                            <div className="prj-info-row">
                                <span className="il">Name</span>
                                <span className="iv">{p.name}</span>
                            </div>
                            {p.customerName && (
                                <div className="prj-info-row">
                                    <span className="il">Builder</span>
                                    <span className="iv">{p.customerName}</span>
                                </div>
                            )}
                            <div className="prj-info-row">
                                <span className="il">Status</span>
                                <span className="iv">
                                    <span className={`prj-pill ${p.active ? "active" : "inactive"}`}>
                                        <span className="dot" />
                                        {p.active ? "Active" : "Inactive"}
                                    </span>
                                </span>
                            </div>
                            {classifDisplay && (
                                <div className="prj-info-row">
                                    <span className="il">Classification</span>
                                    <span className="iv">{classifDisplay}</span>
                                </div>
                            )}
                            <div className="prj-info-row">
                                <span className="il">Prevailing wage</span>
                                <span className="iv">
                                    {p.isPrevailingWage ? "Yes" : "No"}
                                    {showPayGroups && (
                                        <span className={`prj-paygroups${payGroupCount === 0 ? " none" : ""}`}>
                                            ({payGroupLabel})
                                        </span>
                                    )}
                                </span>
                            </div>
                        </div>
                        <div className="prj-panel-h prj-panel-h2">
                            <Users size={15} />
                            <span className="prj-pt">Supervisors</span>
                            <span className="prj-pt-extra">{supervisors.length}</span>
                        </div>
                        <div className="prj-panel-b">
                            {supervisors.length === 0 ? (
                                <div className="prj-sup-empty">No supervisors assigned.</div>
                            ) : (
                                <div className="prj-sup-list">
                                    {supervisors.map((s) => (
                                        <div key={s.employeeID} className="prj-sup">
                                            <div className="prj-av">
                                                {initials(s.name.first, s.name.last)}
                                            </div>
                                            <div>
                                                <div className="prj-sup-name">
                                                    {s.name.first} {s.name.last}
                                                </div>
                                                <div className="prj-sup-sub">{s.altEmployeeID}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Location panel — map-permission gated */}
                    {canAccessMapUi && (
                        <div className="prj-panel">
                            <div className="prj-panel-h">
                                <MapPin size={15} />
                                <span className="prj-pt">Location</span>
                            </div>
                            <div className="prj-panel-b">
                                <div className="prj-info-row">
                                    <span className="il">Address</span>
                                    <span className="iv">{dashOrVal(p.address)}</span>
                                </div>
                                <div className="prj-info-row">
                                    <span className="il">City / State</span>
                                    <span className="iv">
                                        {p.city
                                            ? `${p.city}, ${p.state ?? ""} ${p.zip ?? ""}`.trim()
                                            : <span className="prj-dash">{DASH}</span>}
                                    </span>
                                </div>
                                <div className="prj-info-row">
                                    <span className="il">Cross streets</span>
                                    <span className="iv">{dashOrVal(p.crossStreets)}</span>
                                </div>
                                <div className="prj-info-row">
                                    <span className="il">Latitude</span>
                                    <span className="iv prj-mono">
                                        {hasCoords ? (p.latitude as number).toFixed(6) : <span className="prj-dash">{DASH}</span>}
                                    </span>
                                </div>
                                <div className="prj-info-row">
                                    <span className="il">Longitude</span>
                                    <span className="iv prj-mono">
                                        {hasCoords ? (p.longitude as number).toFixed(6) : <span className="prj-dash">{DASH}</span>}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Geofence panel — map-permission gated */}
                    {canAccessMapUi && (
                        <div className="prj-panel prj-map-panel">
                            <div className="prj-panel-h">
                                <MapPin size={15} />
                                <span className="prj-pt">Geofence</span>
                                {hasCoords && p.radiusMiles && p.radiusMiles > 0 && (
                                    <span className="prj-pt-extra">{p.radiusMiles} mi radius</span>
                                )}
                            </div>
                            {hasCoords ? (
                                <ProjectMapPreview
                                    latitude={p.latitude as number}
                                    longitude={p.longitude as number}
                                    radiusMiles={
                                        typeof p.radiusMiles === "number" && p.radiusMiles > 0
                                            ? p.radiusMiles
                                            : 0
                                    }
                                    onOpen={() => { setGeoModalMode("view"); setGeoModalOpen(true); }}
                                />
                            ) : (
                                <div className="prj-map-none">
                                    <MapPin size={26} />
                                    <div className="prj-map-none-t">No map location</div>
                                    {canEditProjectMap && (
                                        <button
                                            className="prj-btn sm"
                                            onClick={() => { setGeoModalMode("edit"); setGeoModalOpen(true); }}
                                        >
                                            <MapPin size={13} />
                                            Set map location
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* KPI strip */}
                {roll && (
                    <div className="prj-kpi-strip">
                        <div className="prj-kpi">
                            <div className="prj-kpi-lab">
                                <DollarSign size={14} />
                                Original
                            </div>
                            <div className="prj-kpi-val">{DollarUtil.formatDollar(roll.orig)}</div>
                            <div className="prj-kpi-sub">{roll.total} tasks</div>
                        </div>
                        <div className="prj-kpi accent">
                            <div className="prj-kpi-lab">
                                <DollarSign size={14} />
                                Remaining
                            </div>
                            <div className="prj-kpi-val">{DollarUtil.formatDollar(roll.rem)}</div>
                            <div className="prj-kpi-sub">{DollarUtil.formatDollar(claimed)} claimed to date</div>
                        </div>
                        <div className="prj-kpi good">
                            <div className="prj-kpi-lab">
                                <CheckCircle size={14} />
                                Tasks complete
                            </div>
                            <div className="prj-kpi-val">
                                {roll.completed}
                                <span style={{ color: "var(--text-3)", fontSize: "17px" }}>
                                    {" "}/ {roll.total}
                                </span>
                            </div>
                            <div className="prj-kpi-sub">{roll.open} still open</div>
                        </div>
                        <div className="prj-kpi">
                            <div className="prj-kpi-lab">
                                <TrendingUp size={14} />
                                % Complete
                            </div>
                            <div className="prj-kpi-pct">{pct}%</div>
                            <div className="prj-kpi-bar">
                                <span className="prj-kpi-bar-fill" style={{ width: `${pct}%` }} />
                            </div>
                        </div>
                    </div>
                )}

                {/* Breakdown cards */}
                {loadingProject && (
                    <div className="prj-bd-card">
                        <div className="prj-bd-loading">
                            <span className="prj-spin" style={{ width: 18, height: 18 }} />
                            Loading project breakdown…
                        </div>
                    </div>
                )}
                {!loadingProject && projectData && (
                    <>
                        {renderBreakdownCard1(projectData, roll ?? { total: 0, completed: 0, open: 0, orig: 0, rem: 0 })}
                        {renderBreakdownCard2()}
                    </>
                )}
            </>
        );
    };

    // ─────────────────────────────────────────────────────────────────────────
    // ACCESS GATE
    // ─────────────────────────────────────────────────────────────────────────
    if (!config.showProjectsPage) {
        return (
            <PageWrapper title="Projects" backgroundColor="#f4f5f7">
                <div />
            </PageWrapper>
        );
    }

    return (
        <PageWrapper title="Projects" backgroundColor="#f4f5f7">
            <div className="prj">
                <div className="prj-page">
                    {view === "welcome" ? renderWelcome() : renderSelected()}
                </div>

                {/* Sync JC result modal */}
                {syncResultOpen && syncResult && (
                    <div className="prj-modal-bg" onClick={() => setSyncResultOpen(false)}>
                        <div className="prj-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="prj-modal-head">
                                <span className="prj-modal-mi">
                                    <CheckCircle size={21} />
                                </span>
                                <div>
                                    <h3>JC Pieceworks Cache Synced</h3>
                                    <div className="prj-modal-ms">Sync job completed successfully.</div>
                                </div>
                                <button className="prj-modal-x" onClick={() => setSyncResultOpen(false)}>
                                    <X size={16} />
                                </button>
                            </div>
                            <div className="prj-modal-body">
                                <div className="prj-result-grid">
                                    <div className="prj-result-stat added">
                                        <div className="rs-val">{syncResult.added}</div>
                                        <div className="rs-lab">Added</div>
                                    </div>
                                    <div className="prj-result-stat updated">
                                        <div className="rs-val">{syncResult.updated}</div>
                                        <div className="rs-lab">Updated</div>
                                    </div>
                                    <div className="prj-result-stat removed">
                                        <div className="rs-val">{syncResult.removed}</div>
                                        <div className="rs-lab">Removed</div>
                                    </div>
                                </div>
                                <div className="prj-result-lines">
                                    <div className="rl">
                                        <CheckCircle size={16} />
                                        <span className="rl-lab">Job status</span>
                                        <span className="rl-v">{syncResult.status}</span>
                                    </div>
                                    {syncResult.scanned > 0 && (
                                        <div className="rl">
                                            <Database size={16} style={{ color: "var(--text-3)" }} />
                                            <span className="rl-lab">Piece records scanned</span>
                                            <span className="rl-v">{syncResult.scanned.toLocaleString()}</span>
                                        </div>
                                    )}
                                    <div className="rl">
                                        <Clock size={16} style={{ color: "var(--text-3)" }} />
                                        <span className="rl-lab">Elapsed</span>
                                        <span className="rl-v">{Math.floor(syncResult.elapsed / 60)}m {syncResult.elapsed % 60}s</span>
                                    </div>
                                    {syncResult.completedAt && (
                                        <div className="rl">
                                            <Calendar size={16} style={{ color: "var(--text-3)" }} />
                                            <span className="rl-lab">Completed</span>
                                            <span className="rl-v">{fmtDateUTC(syncResult.completedAt)}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="prj-modal-foot">
                                <button className="prj-btn" onClick={() => setSyncResultOpen(false)}>
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Geolocation modal (v2 spec: view + edit modes) */}
                {selectedProject && canAccessMapUi && (
                    <ProjectGeolocationModal
                        isOpen={geoModalOpen}
                        onClose={() => setGeoModalOpen(false)}
                        project={selectedProject}
                        canEdit={canEditProjectMap}
                        initialMode={geoModalMode}
                        onSaved={(updated) => setSelectedProject(updated)}
                        onToast={showToast}
                    />
                )}

                {/* Toasts */}
                {toasts.length > 0 && (
                    <div className="prj-toast-wrap">
                        {toasts.map((t) => (
                            <div key={t.id} className={`prj-toast ${t.kind} ${t.out ? "out" : ""}`}>
                                {t.kind === "ok" ? <CheckCircle size={18} /> : <AlertTriangle size={18} />}
                                <span
                                    className="t-msg"
                                    // msg may include <b> for emphasis — controlled internal content only
                                    dangerouslySetInnerHTML={{ __html: t.msg }}
                                />
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </PageWrapper>
    );
};

export default ProjectsPage;
